import { useEffect, useMemo, useState } from 'react'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { useTurns } from '@/contexts/TurnsProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { SpeakingRole } from '@db8/shared'
import ProfileImage from '../ProfileImage'
import { useHall } from '@/contexts/HallProvider'

interface TeamSelectorProps {
  step: number
  isSelectDisabled: boolean
  speakingRole: SpeakingRole
  defaultUserId: string
  position: 'primary' | 'secondary'
}

export const TeamSelector = ({
  step,
  isSelectDisabled,
  speakingRole,
  defaultUserId,
  position = 'primary',
}: TeamSelectorProps) => {
  const { hallUsers } = useHallUsers()
  const { setTurnUser } = useTurns()
  const [currentSelected, setCurrentSelected] = useState('')

  const filteredHallUsers = useMemo(() => {
    // Allow bots in cross-ex/crossfire slots (AI vs human / AI vs AI).
    // Filtering here should be based on speakingRole, not whether the user is a bot.
    return hallUsers
  }, [hallUsers])

  const eligibleUsers = useMemo(() => {
    // Slot belongs to a team: only users (including bots) on that team are selectable.
    // Bots must be dragged into the team (set speakingRole) to be eligible.
    return filteredHallUsers.filter((user) => user.speakingRole === speakingRole)
  }, [filteredHallUsers, speakingRole])

  useEffect(() => {
    if (defaultUserId) {
      const user = filteredHallUsers.find((user) => user.id === defaultUserId)
      if (!user) return
      setCurrentSelected(JSON.stringify({ userId: defaultUserId, step }))
    }
  }, [defaultUserId, filteredHallUsers, step])

  const handleValueChange = (value: string) => {
    if (speakingRole === SpeakingRole.Affirmative) setTurnUser(value, position)
    else if (speakingRole === SpeakingRole.Negative) setTurnUser(value, position)
  }

  return (
    <Select value={currentSelected} disabled={isSelectDisabled} onValueChange={handleValueChange}>
      <SelectTrigger className="w-full px-2 py-1 text-xs">
        <SelectValue placeholder="Select User" />
      </SelectTrigger>
      <SelectContent className="text-xs">
        <SelectGroup>
          {eligibleUsers.length > 0 ? (
            eligibleUsers.map((user) => {
              const thisValue = JSON.stringify({ userId: user.id, step })
              return (
                <SelectItem key={user.id} value={thisValue}>
                  <div className="text-xs flex items-center gap-2">
                    <ProfileImage user={user} className="w-5 h-5 rounded-full object-cover" />
                    {user.displayName}
                  </div>
                </SelectItem>
              )
            })
          ) : (
            <SelectItem disabled value="no-user" className="text-xs text-center pr-8">
              No eligible users for this slot
            </SelectItem>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}
